import { FormEvent, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import Icon from '@baaligames/shared-ui/components/Icon';

// Structured shape the backend ships in `err.details` for a 401 from
// /auth/login. Mirrors auth.controller.js → login().
type LoginErrDetails =
  | { reason: 'unknown_user' }
  | { reason: 'bad_password'; attemptsLeft: number; maxAttempts: number }
  | { reason: 'locked'; unlockAt: string; minutesLeft: number };

type LoginErrState =
  | { tone: 'warn';   message: string; sublabel?: string }
  | { tone: 'danger'; message: string; sublabel?: string }
  | null;

export default function Login() {
  const t = useT();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<LoginErrState>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      // The user can sign in with username, email, or 10-digit mobile.
      // We auto-prepend +91 if they entered exactly 10 digits, so it
      // matches how phone numbers are stored.
      const id = identifier.trim();
      const sendId = /^\d{10}$/.test(id) ? '+91' + id : id;
      await login(sendId, password);
      nav(loc.state?.from || '/', { replace: true });
    } catch (e) {
      // 2026-05-21 — Translate the structured 401 details into a
      // human-friendly two-line error. Big distinction:
      //   • bad_password → amber warning with attempts-left counter
      //     ("4 attempts remaining" on the first wrong try, counting
      //     down as the player guesses). Replaces the flat "Invalid
      //     credentials" we had before.
      //   • locked → red danger with minutes-remaining (so the
      //     player doesn't keep tapping submit on a locked account).
      //   • unknown_user → keep generic to avoid leaking account
      //     existence to a scraper.
      const err = e as ApiError;
      const d = err.details as LoginErrDetails | undefined;
      if (d?.reason === 'bad_password') {
        setError({
          tone: 'warn',
          message: 'Wrong password',
          sublabel: `${d.attemptsLeft} attempt${d.attemptsLeft === 1 ? '' : 's'} remaining before lockout.`,
        });
      } else if (d?.reason === 'locked') {
        setError({
          tone: 'danger',
          message: `Account locked for ~${d.minutesLeft} min`,
          sublabel: 'Too many wrong passwords. You can try again once the lockout ends, or use the link below to reset.',
        });
      } else {
        setError({ tone: 'warn', message: err.message || 'Could not sign in', sublabel: undefined });
      }
    } finally { setLoading(false); }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-head-badge">
            <Icon name="sports_esports" size={22} />
          </div>
          <h1 className="auth-h1">{t('login.title')}</h1>
          <p className="auth-sub">{t('login.subtitle')}</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label" htmlFor="login-id">{t('login.idLabel')}</label>
            <div className="auth-field">
              <Icon name="person" className="auth-field-icon" />
              <input
                id="login-id"
                className="auth-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('login.idPlaceholder')}
                autoComplete="username"
                maxLength={120}
                required
                autoFocus
              />
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label className="label" htmlFor="login-pwd">{t('login.passwordLabel')}</label>
              <Link to="/forgot-password" className="auth-link text-[11px]">{t('login.forgot')}</Link>
            </div>
            <div className="auth-field">
              <Icon name="lock" className="auth-field-icon" />
              <input
                id="login-pwd"
                className="auth-input"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
                maxLength={72}
                required
              />
              <button
                type="button"
                className="auth-field-trail min-w-[44px] min-h-[44px] grid place-items-center"
                aria-label={showPwd ? 'Hide password' : 'Show password'}
                onClick={() => setShowPwd((v) => !v)}
              >
                <Icon name={showPwd ? 'visibility_off' : 'visibility'} />
              </button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className={`rounded-xl px-3 py-2.5 flex items-start gap-2 border
                ${error.tone === 'danger'
                  ? 'border-rose-300 dark:border-rose-400/40 bg-rose-50/80 dark:bg-rose-500/10 text-rose-800 dark:text-rose-200'
                  : 'border-amber-300 dark:border-amber-400/40 bg-amber-50/80 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100'}`}>
              <Icon
                name={error.tone === 'danger' ? 'lock_clock' : 'error'}
                size={18}
                filled
                className="shrink-0 mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold leading-tight">{error.message}</div>
                {error.sublabel && (
                  <div className="text-[11.5px] mt-0.5 opacity-80 leading-snug">
                    {error.sublabel}
                  </div>
                )}
              </div>
            </div>
          )}

          <button type="submit" className="auth-cta mt-1" disabled={loading}>
            {loading ? (
              <>
                <Icon name="progress_activity" className="animate-spin" />
                {t('login.signingIn')}
              </>
            ) : (
              <>{t('login.cta')} <Icon name="arrow_forward" /></>
            )}
          </button>
        </form>

        <div className="auth-foot">
          {t('login.newHere')} <Link to="/register" className="auth-link">{t('login.createAccount')}</Link>
        </div>
      </div>
    </div>
  );
}
